/**
 * Retrying fetch wrapper for the cold-start case on Azure SQL Serverless.
 *
 * The DB auto-pauses after 60 minutes idle; the first request after that takes
 * 30–60s to resume. During business hours the keep-warm GitHub Action prevents
 * this, but off-hours hits (3 AM sick-day submissions, weekend admin work)
 * still pay the cost. Without this helper, those requests would surface as
 * 500 / 504 errors after ~30–45s.
 *
 * Behavior:
 *   - Each attempt has its own AbortController timeout (per-attempt budget).
 *   - On a 5xx response, network error, or per-attempt timeout, we wait
 *     `retryDelayMs` (default 5s) then retry.
 *   - We keep retrying until either a 2xx/3xx/4xx arrives or `maxTotalMs`
 *     wall-clock budget is exhausted (default 90s).
 *   - 4xx is treated as terminal (real client error — don't mask it).
 *   - On the first failure, optional `onWarming` callback fires so the UI can
 *     swap from "Loading…" to "Waking up the database…".
 *
 * Only safe to use for idempotent requests (GET, HEAD). Don't wrap POSTs that
 * mutate state — a duplicated successful POST during a coincidental timeout
 * would create a duplicate record.
 */
export interface RetryOptions {
  onWarming?: () => void;
  maxTotalMs?: number;
  perAttemptMs?: number;
  retryDelayMs?: number;
}

export async function fetchWithRetry(
  input: RequestInfo | URL,
  init: RequestInit = {},
  opts: RetryOptions = {}
): Promise<Response> {
  const maxTotal = opts.maxTotalMs ?? 90_000;
  const perAttempt = opts.perAttemptMs ?? 30_000;
  const retryDelay = opts.retryDelayMs ?? 5_000;
  const start = Date.now();
  let attempt = 0;
  let warmingFired = false;
  let lastError: unknown = null;

  while (Date.now() - start < maxTotal) {
    attempt++;
    const remaining = maxTotal - (Date.now() - start);
    const thisAttempt = Math.min(perAttempt, remaining);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), thisAttempt);
    try {
      const r = await fetch(input, { ...init, signal: controller.signal });
      clearTimeout(timer);
      if (r.status >= 500 && r.status < 600) {
        lastError = new Error(`HTTP ${r.status}`);
        if (!warmingFired && opts.onWarming) {
          opts.onWarming();
          warmingFired = true;
        }
        if (Date.now() - start + retryDelay >= maxTotal) break;
        await sleep(retryDelay);
        continue;
      }
      return r;
    } catch (err) {
      clearTimeout(timer);
      lastError = err;
      if (!warmingFired && opts.onWarming) {
        opts.onWarming();
        warmingFired = true;
      }
      if (Date.now() - start + retryDelay >= maxTotal) break;
      await sleep(retryDelay);
    }
  }
  throw lastError instanceof Error ? lastError : new Error(`Request failed after ${attempt} attempt(s)`);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
